import { useState, useEffect, useCallback } from 'react'
import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { ScoopTextField } from '../ScoopTextField/ScoopTextField'
import styles from './ScoopSearchBar.module.scss'

/**
 * ScoopSearchBar - Search input component with icon and debounce
 * Uses ScoopTextField internally with a search icon
 *
 * @param {string} placeholder - Placeholder text (default: 'Search...')
 * @param {string} value - Search value (controlled)
 * @param {function} onChange - Change handler (debounced by default)
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300ms, set to 0 to disable)
 * @param {string} className - Additional CSS classes for the container
 * @param {boolean} disabled - If true, search bar is disabled
 * @param {boolean} noIcon - If true, hides the search icon
 * @param {function} onSearch - Optional callback when search icon is clicked
 * @param {string|number} width - Width of the search bar (default: '360px')
 * @param {string|number} height - Height of the search bar (default: '43px')
 * @param {object} inputStyle - Additional inline styles for the input element
 * @param {object} ...props - Additional props passed to ScoopTextField
 */
export const ScoopSearchBar = ({
    placeholder = 'Search...',
    value,
    onChange,
    debounceMs = 200,
    className,
    disabled,
    noIcon,
    onSearch,
    width = '360px',
    height = '43px',
    inputStyle,
    ...props
}) => {
    const [internalValue, setInternalValue] = useState(value || '')

    // Update internal value when external value changes
    useEffect(() => {
        setInternalValue(value || '')
    }, [value])

    // Debounced onChange
    useEffect(() => {
        if (debounceMs === 0) return // Skip debounce if disabled

        const timer = setTimeout(() => {
            if (onChange && internalValue !== value) {
                onChange({ target: { value: internalValue } })
            }
        }, debounceMs)

        return () => clearTimeout(timer)
    }, [internalValue, debounceMs, onChange, value])

    const handleChange = useCallback(
        (e) => {
            const newValue = e.target.value
            setInternalValue(newValue)

            // If debounce is disabled, call onChange immediately
            if (debounceMs === 0 && onChange) {
                onChange(e)
            }
        },
        [debounceMs, onChange]
    )
    const handleIconClick = () => {
        if (onSearch && !disabled) {
            onSearch(internalValue)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSearch && !disabled) {
            onSearch(internalValue)
        }
    }

    const containerStyle = {
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
    }

    return (
        <div className={`${styles.searchBarContainer} ${className || ''}`} style={containerStyle}>
            <ScoopTextField
                {...props}
                type="text"
                placeholder={placeholder}
                value={internalValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                disableMargin
                containerClassName={styles.textFieldContainer}
                className={styles.searchInput}
                style={{ height: '100%', ...inputStyle }}
            />
            {!noIcon && (
                <button
                    type="button"
                    className={styles.searchIcon}
                    onClick={handleIconClick}
                    disabled={disabled}
                    aria-label="Search"
                >
                    <MagnifyingGlassIcon size={20} weight="regular" />
                </button>
            )}
        </div>
    )
}

ScoopSearchBar.displayName = 'ScoopSearchBar'
